import { useEffect, useRef, useState } from 'react';
import { t } from '../../platform/lang';

/**
 * Digital signature capture — HTML5 Canvas + Pointer Events.
 *
 * TWO separate faults have been reported here, and the first fix addressed
 * neither of them. Writing both down because the second one was invisible for
 * two days.
 *
 * 1. THE CANVAS WAS SIZED ONCE, AT MOUNT, FROM A LAYOUT THAT HAD NOT SETTLED.
 *
 *    A canvas has two sizes: the box it occupies on screen, and its backing
 *    store in pixels. They have to agree or every coordinate is wrong.
 *
 *    This measured `getBoundingClientRect()` in a mount effect and never again.
 *    In the rebuilt flow the signature step mounts during a step transition, so
 *    the box is frequently 0 at that instant — and the old code quietly fell
 *    back to 300x180 while CSS displayed it at full width by 240. Touches were
 *    then recorded against a box a different size from the one being drawn in,
 *    so ink landed somewhere other than the fingertip, or off the canvas
 *    entirely. RELOADING THE PAGE FIXED IT, because after a reload the layout
 *    was already settled when the effect ran — which is exactly the symptom
 *    that was reported, twice.
 *
 *    It is now measured with a ResizeObserver, so it is correct whenever the
 *    box actually changes, including rotation and the iOS toolbar sliding away.
 *    Existing ink is preserved across a resize; losing a half-finished
 *    signature to a rotation would be its own bug.
 *
 * 2. `drawing` COULD GET STUCK ON.
 *
 *    There was no pointercancel or touchcancel handler. iOS fires those
 *    liberally — a second finger, a system gesture, the toolbar — and NOT
 *    pointerup. When that happened `drawing` stayed true, and because the touch
 *    handler refuses to start while it is true, the pad went dead until the
 *    component was remounted. Another "fixed by reloading".
 *
 * Both now have handlers. Nothing here asserts a ref is non-null: Safari's
 * answer to a wrong assertion is "null is not an object", which a driver has
 * already seen once.
 */
export function SignaturePad(props: {
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
  /**
   * At the warehouse nobody "receives" — the driver does. The line above the
   * pad asks for the signature of whoever HANDED THE TOTES OVER instead, which
   * is both answerable and the more useful record: it is who released the
   * medication. Defaults to the delivery wording.
   */
  atPickup?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  /*
   * The same fact as `hasInk`, readable from inside the resize effect without
   * making that effect depend on it -- re-running it would tear down the
   * ResizeObserver every time the first stroke landed.
   */
  const inkRef = useRef(false);
  const markInk = (v: boolean) => { inkRef.current = v; setHasInk(v); };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    /** Match the backing store to the box, keeping whatever is already drawn. */
    const fit = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      // Still not laid out. Do nothing rather than guess: the observer will
      // call again the moment it has a real size, and a wrong guess is what
      // caused the bug above.
      if (w === 0 || h === 0) return;

      const want = { w: Math.round(w * dpr), h: Math.round(h * dpr) };
      if (canvas.width === want.w && canvas.height === want.h) return;

      /*
       * THE BLACK SQUARE IN THE TOP-LEFT CORNER.
       *
       * Resizing a canvas clears it, so the ink has to be kept. This used
       * getImageData/putImageData, and putImageData does not DRAW pixels, it
       * REPLACES them -- alpha included, ignoring any transform.
       *
       * On the first fit() the canvas is still at its untouched default backing
       * store of 300x150, which is fully TRANSPARENT and has never been painted
       * white. That transparent block was then stamped straight into the corner
       * of the freshly whitened canvas, punching a 300x150 hole in it. On a
       * phone in dark mode the page behind shows through, which is the black
       * rectangle in the signature box.
       *
       * It is not cosmetic: the saved PNG carries that hole, so any proof of
       * delivery rendered on a dark background has a black block over the
       * corner of the signature, and a recipient who signs small in that corner
       * signs onto nothing.
       *
       * Two changes. Nothing is restored unless there is real ink to restore --
       * the case that caused this had none. And the copy goes through
       * drawImage, which composites and scales properly, so a rotation or the
       * iOS toolbar sliding away keeps the signature where the person put it.
       */
      let snapshot: HTMLCanvasElement | null = null;
      if (inkRef.current && canvas.width > 0 && canvas.height > 0) {
        snapshot = document.createElement('canvas');
        snapshot.width = canvas.width;
        snapshot.height = canvas.height;
        snapshot.getContext('2d')?.drawImage(canvas, 0, 0);
      }

      canvas.width = want.w;
      canvas.height = want.h;

      const c = canvas.getContext('2d');
      if (!c) return;
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.fillStyle = '#fff';
      c.fillRect(0, 0, want.w, want.h);
      if (snapshot) c.drawImage(snapshot, 0, 0, want.w, want.h);
      c.scale(dpr, dpr);
      c.lineWidth = 2.5;
      c.lineCap = 'round';
      c.lineJoin = 'round';
      c.strokeStyle = '#111';
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  /** Where the touch landed, in the same coordinate space we drew the box in. */
  const pointFrom = (clientX: number, clientY: number) => {
    const el = canvasRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return null;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const beginAt = (clientX: number, clientY: number) => {
    const ctx = canvasRef.current?.getContext('2d');
    const pt = pointFrom(clientX, clientY);
    if (!ctx || !pt) return;
    drawing.current = true;
    ctx.beginPath();
    ctx.moveTo(pt.x, pt.y);
  };

  const drawTo = (clientX: number, clientY: number) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    const pt = pointFrom(clientX, clientY);
    if (!ctx || !pt) return;
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    if (!inkRef.current) markInk(true);
  };

  /*
   * Called by pointerup, pointercancel, pointerleave, touchend AND touchcancel.
   * Every one of them, always: a missed "finger is up" is what froze the pad.
   */
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();          // drop any half-built path, or it re-strokes
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, rect.width || canvas.width, rect.height || canvas.height);
    drawing.current = false;
    markInk(false);
  };

  const save = () => {
    canvasRef.current?.toBlob(
      (blob) => { if (blob) props.onCapture(blob); }, 'image/png');
  };

  return (
    <div className="signature-pad">
      <p className="hint">{t(props.atPickup ? 'sigWhoPickup' : 'sigWho')}</p>
      {/*
        * Pointer events are the primary path and handle touch, stylus and mouse
        * together. The touch handlers stay as a fallback for the older phones in
        * this fleet, guarded by `drawing` so a device firing both kinds does not
        * draw every stroke twice.
        */}
      <canvas
        ref={canvasRef}
        className="signature-canvas"
        onPointerDown={(e) => { e.preventDefault(); beginAt(e.clientX, e.clientY); }}
        onPointerMove={(e) => drawTo(e.clientX, e.clientY)}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
        onTouchStart={(e) => {
          if (drawing.current) return;
          const tch = e.touches[0];
          if (!tch) return;
          e.preventDefault();
          beginAt(tch.clientX, tch.clientY);
        }}
        onTouchMove={(e) => {
          if (!drawing.current) return;
          const tch = e.touches[0];
          if (!tch) return;
          e.preventDefault();
          drawTo(tch.clientX, tch.clientY);
        }}
        onTouchEnd={end}
        onTouchCancel={end}
      />
      <div className="row">
        <button className="btn secondary" onClick={props.onCancel}>{t('sigCancel')}</button>
        <button className="btn secondary" onClick={clear}>{t('sigClear')}</button>
        <button className="btn primary" disabled={!hasInk} onClick={save}>{t('sigUse')}</button>
      </div>
    </div>
  );
}
